import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SaveButton from '../../components/SaveButton';
import CancelFormButton from '../../components/CancelFormButton';
import useNotifier from '../../hooks/useNotifier';
import { gql, useMutation } from '@apollo/client';
import { Account_AccountData_account } from '../../types/graph/Account_AccountData';
import {
  ExclamationCircleIcon,
  EyeIcon,
  EyeOffIcon,
} from '@heroicons/react/solid';
interface NewPasswordValues {
  newPassword: string;
  confirmNewPassword: string;
}

interface IProps {
  account: Account_AccountData_account;
}

const validationSchema = yup.object().shape({
  newPassword: yup.string().required('Password is required'),
  // .min(6, 'Must be at least 6 characters long'),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
});

const AccountPassword: React.FC<IProps> = ({ account }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const notifier = useNotifier();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset: resetForm,
  } = useForm<NewPasswordValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const [updateUser] = useMutation(UPDATE_ACCOUNT_USER, {
    onCompleted: () => {
      setLoading(false);
      notifier.showMessage(
        'Successfully saved!',
        'Your account password has been updated',
        'success'
      );
      resetForm();
    },
  });

  const onSubmit = (values: NewPasswordValues) => {
    setLoading(true);
    updateUser({
      variables: {
        accountId: account.id,
        userId: account.user!.id,
        user: {
          password: values.newPassword,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="h-full shadow rounded-lg bg-white">
        <div className="py-6 px-4 sm:p-6 lg:pb-8">
          <div>
            <h2 className="text-lg leading-6 font-medium text-gray-900">
              Password
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Set a new password for your user account.
            </p>
          </div>

          <div className="mt-6 grid grid-cols-8 gap-6">
            <div className="col-span-8 sm:col-start-3 sm:col-span-4">
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <div className="mt-1 relative">
                <input
                  autoComplete="off"
                  {...register('newPassword')}
                  type={viewPassword ? 'text' : 'password'}
                  id="newPassword"
                  className={`${
                    errors.newPassword
                      ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
                      : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
                  } appearance-none block w-full px-3 py-2 border  rounded-md shadow-sm  focus:outline-none  sm:text-sm`}
                />
                <div
                  className={`${
                    errors.newPassword && '-top-7'
                  } absolute inset-y-0 right-0 pr-3 flex items-center`}
                >
                  {viewPassword ? (
                    <EyeIcon
                      className="h-5 w-5 text-gray-400 cursor-pointer"
                      aria-hidden="true"
                      onClick={() => setViewPassword(!viewPassword)}
                    />
                  ) : (
                    <EyeOffIcon
                      className="h-5 w-5 text-gray-400 cursor-pointer"
                      aria-hidden="true"
                      onClick={() => setViewPassword(!viewPassword)}
                    />
                  )}
                </div>
                {errors.newPassword && (
                  <>
                    <div className="absolute inset-y-0 -top-7 right-7 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon
                        className="h-5 w-5 text-red-500"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-2 text-sm text-red-600">
                      {errors.newPassword.message}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="col-span-8 sm:col-start-3 sm:col-span-4">
              <label
                htmlFor="confirmNewPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <div className="mt-1 relative">
                <input
                  autoComplete="off"
                  {...register('confirmNewPassword')}
                  type={viewPassword ? 'text' : 'password'}
                  id="confirmNewPassword"
                  className={`${
                    errors.confirmNewPassword
                      ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
                      : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
                  } appearance-none block w-full px-3 py-2 border  rounded-md shadow-sm  focus:outline-none  sm:text-sm`}
                />
                <div
                  className={`${
                    errors.confirmNewPassword && '-top-7'
                  } absolute inset-y-0 right-0 pr-3 flex items-center`}
                >
                  {viewPassword ? (
                    <EyeIcon
                      className="h-5 w-5 text-gray-400 cursor-pointer"
                      aria-hidden="true"
                      onClick={() => setViewPassword(!viewPassword)}
                    />
                  ) : (
                    <EyeOffIcon
                      className="h-5 w-5 text-gray-400 cursor-pointer"
                      aria-hidden="true"
                      onClick={() => setViewPassword(!viewPassword)}
                    />
                  )}
                </div>
                {errors.confirmNewPassword && (
                  <>
                    <div className="absolute inset-y-0 -top-7 right-7 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon
                        className="h-5 w-5 text-red-500"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-2 text-sm text-red-600">
                      {errors.confirmNewPassword.message}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 rounded-b-lg">
          <CancelFormButton
            hasChanges={isDirty}
            disabled={!isDirty}
            onCleanUp={() => resetForm()}
          />

          <SaveButton loading={loading} />
        </div>
      </div>
    </form>
  );
};

export default AccountPassword;

const UPDATE_ACCOUNT_USER = gql`
  mutation AccountPassword_UpdateAccountUser(
    $accountId: String!
    $userId: String!
    $user: UserInput!
  ) {
    accountUserUpdate(accountId: $accountId, userId: $userId, user: $user) {
      id
      scope
      state {
        status
        setAt
      }
      roles
      user {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
      users {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
    }
  }
`;
